import { create } from 'zustand';
import { Issue, IssueCreate, IssueUpdate } from '../types/issue';
import { issueApi, GetIssuesParams } from '../api/issueApi';
import { handleApiError } from '../utils/error';

interface IssueStore {
  issues: Issue[];
  loading: boolean;
  error: string | null;
  fetchIssues: (params?: GetIssuesParams) => Promise<void>;
  createIssue: (data: IssueCreate) => Promise<Issue>;
  updateIssue: (id: number, data: IssueUpdate, projectId?: number) => Promise<Issue>;
  deleteIssue: (id: number, projectId?: number) => Promise<void>;
  clearIssues: () => void;
}

export const useIssueStore = create<IssueStore>((set, get) => ({
  issues: [],
  loading: false,
  error: null,

  fetchIssues: async (params) => {
    set({ loading: true, error: null });
    try {
      const data = await issueApi.getIssues(params);
      set({ issues: data, loading: false });
    } catch (error) {
      console.error('Error in fetchIssues:', error);
      set({ error: handleApiError(error), loading: false });
    }
  },

  createIssue: async (data) => {
    set({ loading: true, error: null });
    try {
      console.log('Creating issue with data:', data);
      const newIssue = await issueApi.createIssue(data);
      set((state) => ({
        issues: [...state.issues, newIssue],
        loading: false,
      }));
      return newIssue;
    } catch (error) {
      console.error('Error in createIssue:', error);
      set({ error: handleApiError(error), loading: false });
      throw error;
    }
  },

  updateIssue: async (id, data, projectId) => {
    set({ loading: true, error: null });
    try {
      const updatedIssue = await issueApi.updateIssue(id, data, projectId);
      set((state) => ({
        issues: state.issues.map((issue) =>
          issue.id === id ? updatedIssue : issue
        ),
        loading: false,
      }));
      return updatedIssue;
    } catch (error) {
      console.error('Error in updateIssue:', error);
      set({ error: handleApiError(error), loading: false });
      throw error;
    }
  },

  deleteIssue: async (id, projectId) => {
    set({ loading: true, error: null });
    try {
      await issueApi.deleteIssue(id, projectId);
      set((state) => ({
        issues: state.issues.filter((issue) => issue.id !== id),
        loading: false,
      }));
    } catch (error) {
      console.error('Error in deleteIssue:', error);
      set({ error: handleApiError(error), loading: false });
      throw error;
    }
  },

  clearIssues: () => {
    set({ issues: [], error: null });
  },
}));
